import { cn } from "@gymlog/shared";

interface LogoProps {
  size?: "sm" | "lg";
  class?: string;
}

/**
 * GymLog brand mark — a stylised dumbbell built from primitives so it renders
 * crisp at every size and inherits the theme accent. Replaces the generic
 * CheckSquare inherited from the template.
 */
function DumbbellMark(props: { class?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      class={props.class}
      stroke="currentColor"
      stroke-width="2.4"
      stroke-linecap="round"
      stroke-linejoin="round"
    >
      {/* Bar */}
      <path d="M7 12h10" />
      {/* Left plates */}
      <path d="M5 8.5v7" />
      <path d="M2.6 10v4" />
      {/* Right plates */}
      <path d="M19 8.5v7" />
      <path d="M21.4 10v4" />
    </svg>
  );
}

export function Logo(props: LogoProps) {
  const size = () => props.size ?? "sm";

  return (
    <div
      class={cn(
        "flex items-center gap-3 select-none",
        size() === "lg" && "flex-col gap-4 text-center",
        props.class,
      )}
    >
      {size() === "sm" ? (
        <>
          <div class="h-9 w-9 rounded-xl bg-accent text-accent-foreground shadow-lg shadow-accent/30 flex items-center justify-center shrink-0">
            <DumbbellMark class="h-5 w-5" />
          </div>
          {/* Wordmark stays on foreground (contrast-paired vs background) for
              guaranteed legibility in every theme; brand accent lives in the
              solid chip. "Gym" is heavier so the wordmark still reads as two-tone. */}
          <span
            class="text-2xl uppercase tracking-tight leading-none text-foreground"
            style={{
              "font-family": "var(--font-display)",
              "font-weight": "700",
            }}
          >
            Gym
            <span class="text-foreground/65" style={{ "font-weight": "600" }}>
              log
            </span>
          </span>
        </>
      ) : (
        <>
          {/* Mark sits on a SOLID accent chip so the dumbbell is guaranteed to
              contrast against the chip in every theme (accent vs accent-foreground
              is contrast-paired by the token system). */}
          <div class="h-16 w-16 rounded-2xl bg-accent text-accent-foreground ring-1 ring-accent/40 flex items-center justify-center mb-2 shadow-lg shadow-accent/30 shrink-0 transition-transform hover:scale-105 active:scale-95 duration-300">
            <DumbbellMark class="h-9 w-9" />
          </div>
          {/* Wordmark uses foreground (contrast-paired vs background) so the brand
              is always legible; the accent lives in the solid chip above. */}
          <h1
            class="text-4xl uppercase tracking-tight leading-none text-foreground"
            style={{
              "font-family": "var(--font-display)",
              "font-weight": "700",
            }}
          >
            Gym
            <span class="text-foreground/65" style={{ "font-weight": "600" }}>
              log
            </span>
          </h1>
        </>
      )}
    </div>
  );
}
